import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { validateStep } from "../validation";
import { validateAffiliatedGroupRow, validateOwnerRow, validateOwnershipAssignmentRow, isBlankOwnerRow, OWNER_REQUIRED_MESSAGE } from "../validation/rules";
import { TOTAL_STEPS } from "../types";
import { isRequiredDocsVisible, isOwnerEligibilityVisible } from "../hooks/step7Rules";
import { Alert } from "../components/Toast";
import { stackedList } from "../utils/reviewHelpers";
import { ownerDisplayName } from "../utils/ownerDisplayName";
import type { Mismatch, Owner } from "../types";
import { MULTI_EMPLOYER_LABELS, COMMONLY_CONTROLLED, PEO_LABELS, PRIMARY_ENTITY_ID, isAffiliatedListShown, isOwnershipExempt } from "../types/structureConstants";
import { buildEntityBlocks, buildOwnershipBlocks, entityHeaderLabel } from "./companyStructureBlocks";
import type { useStepValidation } from "../hooks";

const ownerName = (o: Owner, i: number): string => ownerDisplayName(o, `Owner ${i + 1}`);

interface StepReviewProps {
  validation: ReturnType<typeof useStepValidation>;
}

// ─── Shared ReviewField helper ──────────────────────────────────────────────

interface ReviewFieldProps {
  label: string;
  value: React.ReactNode;
  fieldKey?: string;
  allErrors?: Record<string, string>;
  mismatches?: Record<string, Mismatch>;
  showErrors?: boolean;
  /** Below the `md` breakpoint, stack label-above-value with the value going
   *  full-width (still right-aligned) — used for long emails that wrap awkwardly. */
  stackOnMobile?: boolean;
  /** Indent the label only (not the row background) so nested rows under an
   *  owner/affiliated-entity header still highlight full width on error. */
  nested?: boolean;
  /** Force the row's error styling (red background, red label + value) without
   *  rendering an inline error message. Used to visually flag rows that share
   *  a single aggregate error message rendered separately below the group —
   *  e.g. affiliated entities' Covered Under Policy rows when the
   *  "at least one covered" cross-row rule fires. */
  danger?: boolean;
}

export function ReviewField({
  label,
  value,
  fieldKey,
  allErrors = {},
  mismatches = {},
  showErrors = false,
  stackOnMobile = false,
  nested = false,
  danger = false,
}: ReviewFieldProps) {
  const mismatch = fieldKey ? mismatches[fieldKey] : undefined;
  const error = fieldKey && showErrors ? allErrors[fieldKey] : undefined;
  const isEmpty = value === null || value === undefined || value === "";
  const displayValue: React.ReactNode = isEmpty ? "—" : value;
  const hasError = !!error || danger;
  const hasMismatch = !!mismatch && !hasError;

  const rowLayout = stackOnMobile
    ? "flex flex-col md:flex-row md:justify-between md:items-start"
    : "flex justify-between items-start";
  const valueLayout = stackOnMobile
    ? "text-right w-full md:w-auto md:shrink-0 md:max-w-[55%] break-words"
    : "text-right shrink-0 max-w-[55%] break-words";

  return (
    <div
      data-error={hasError ? "true" : undefined}
      className={`${rowLayout} py-2 px-3 ${stackOnMobile ? "md:gap-4" : "gap-4"} border-b border-gray-50 last:border-b-0 ${
        hasError ? "bg-danger-bg" : hasMismatch ? "bg-yellow-50" : ""
      }`}
    >
      <span className={`text-sm break-words ${nested ? "pl-7" : ""} ${hasError ? "text-danger font-semibold" : hasMismatch ? "text-amber-800 font-semibold" : "text-gray-500"}`}>
        {label}
      </span>
      <div className={valueLayout}>
        <div className={`text-sm font-semibold ${hasError ? "text-danger" : hasMismatch ? "text-amber-800" : "text-gray-800"}`}>{displayValue}</div>
        {hasMismatch && mismatch.original && (
          <span className="block text-xs text-amber-700 line-through opacity-70">{mismatch.original}</span>
        )}
        {hasError && <div className="text-xs text-danger mt-0.5">{error}</div>}
      </div>
    </div>
  );
}

// ─── Section wrapper ─────────────────────────────────────────────────────────

interface ReviewSectionProps {
  title: string;
  step: number;
  children: React.ReactNode;
  onEdit: (step: number) => void;
}

function ReviewSection({ title, step, children, onEdit }: ReviewSectionProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
      <div className="flex items-center justify-between bg-brand-green-light border-b border-brand-green px-3 py-2">
        <h2 className="text-xs font-bold text-brand-navy uppercase tracking-wider">{title}</h2>
        <button
          type="button"
          onClick={() => onEdit(step)}
          className="w-[26px] h-[26px] rounded-full bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-green-50 shrink-0"
          aria-label={`Edit ${title}`}
        >
          <Pencil size={13} />
        </button>
      </div>
      <div className="py-1">{children}</div>
    </div>
  );
}

// ─── StepReview ──────────────────────────────────────────────────────────────

export function StepReview({ validation: _validation }: StepReviewProps) {
  const data = useFormStore((s) => s.data);
  const mismatches = useFormStore((s) => s.mismatches);
  const setStep = useFormStore((s) => s.setStep);
  const uwMethod = useFormStore((s) => s.meta.uwMethod);

  const globalTouchCounter = useUiStore((s) => s.validationTouchCounter);
  const [mountCounter] = useState(globalTouchCounter);
  const showErrors = globalTouchCounter > mountCounter;

  const allErrors = useMemo(() => {
    if (!showErrors) return {};
    const errors: Record<string, string> = {};
    for (let s = 1; s <= TOTAL_STEPS; s++) {
      Object.assign(errors, validateStep(s, data).errors);
    }
    // Split the aggregate "owners" / "affiliatedGroups" / "ownershipAssignments"
    // errors into per-field synthetic keys so each invalid row highlights
    // independently. Per-row rules come from validation/rules.ts so the
    // EIN regex / required checks aren't duplicated here.
    delete errors.owners;
    delete errors.ownershipAssignments;
    // Mirror the step-3 schema's AB#3567 exemption (Nonprofit/Other): no minimum
    // roster, blank scaffold rows skipped, no required entities, no 100% totals.
    // Rows that carry actual data still validate for completeness. Keeping this
    // in lockstep with schemas.ts is what AB#3572 was about — the two layers had
    // drifted and Review painted uncorrectable red ownership rows.
    const ownershipExempt = isOwnershipExempt(data);
    if (data.owners.length === 0) {
      if (!ownershipExempt) {
        errors.owners = OWNER_REQUIRED_MESSAGE;
      }
    } else {
      data.owners.forEach((o, i) => {
        if (ownershipExempt && isBlankOwnerRow(o)) return;
        for (const [field, msg] of Object.entries(validateOwnerRow(o))) {
          errors[`owners.${i}.${field}`] = msg;
        }
      });
    }
    if (isAffiliatedListShown(data)) {
      data.affiliatedGroups.forEach((g, i) => {
        for (const [field, msg] of Object.entries(validateAffiliatedGroupRow(g))) {
          errors[`affiliatedGroups.${i}.${field}`] = msg;
        }
      });
    }
    // Ownership Breakdown per-entity totals + per-row completeness. Exempt org
    // types have no required entities (matches requiredEntityIds in schemas.ts),
    // which also disables the zero-rows and 100%-total errors below.
    const requiredEntityIds: string[] = ownershipExempt ? [] : [PRIMARY_ENTITY_ID];
    if (!ownershipExempt && data.isMultiEmployer === "Yes" && data.multiEmployerType === COMMONLY_CONTROLLED) {
      for (const g of data.affiliatedGroups) if (g.covered === true) requiredEntityIds.push(g.id);
    }
    // Same owner enrolling under more than one visible entity is a conflict —
    // mirror the schema rule so each affected row lights up on Review, not just
    // the aggregate error that used to hide behind the "see text in red" toast.
    const requiredIdSet = new Set(requiredEntityIds);
    const enrollingCountsByOwner = new Map<string, number>();
    for (const a of data.ownershipAssignments) {
      if (a.enrollingInBenefits !== true) continue;
      if (!a.ownerId || !requiredIdSet.has(a.entityId)) continue;
      enrollingCountsByOwner.set(a.ownerId, (enrollingCountsByOwner.get(a.ownerId) ?? 0) + 1);
    }
    data.ownershipAssignments.forEach((a) => {
      const rowErrors = validateOwnershipAssignmentRow(a);
      for (const [field, msg] of Object.entries(rowErrors)) {
        errors[`ownershipAssignments.${a.id}.${field}`] = msg;
      }
      const isMultiEnroll = a.enrollingInBenefits === true
        && !!a.ownerId
        && requiredIdSet.has(a.entityId)
        && (enrollingCountsByOwner.get(a.ownerId) ?? 0) > 1;
      if (isMultiEnroll) {
        errors[`ownershipAssignments.${a.id}.multiEnroll`] = "An owner cannot be enrolling under multiple entities.";
      }
      // Aggregate row-level error surfaced by the Ownership Breakdown row.
      // Priority mirrors submit-time severity: cross-row conflict > missing Y/N
      // selectors > ownership % > owner. First non-empty message wins so each
      // affected row shows exactly one line of red text.
      const rowError = errors[`ownershipAssignments.${a.id}.multiEnroll`]
        ?? errors[`ownershipAssignments.${a.id}.receivingW2Wages`]
        ?? errors[`ownershipAssignments.${a.id}.enrollingInBenefits`]
        ?? errors[`ownershipAssignments.${a.id}.ownership`]
        ?? errors[`ownershipAssignments.${a.id}.ownerId`];
      if (rowError) errors[`ownershipAssignments.${a.id}.row`] = rowError;
    });
    for (const entityId of requiredEntityIds) {
      const rows = data.ownershipAssignments.filter((a) => a.entityId === entityId);
      const total = rows.reduce((sum, r) => sum + (Number(r.ownership) || 0), 0);
      const rounded = Math.round(total * 100) / 100;
      if (rows.length === 0) {
        errors[`ownershipAssignments.${entityId}.total`] = OWNER_REQUIRED_MESSAGE;
      } else if (rounded !== 100) {
        errors[`ownershipAssignments.${entityId}.total`] = `Ownership must total 100% (currently ${rounded}%)`;
      }
    }
    return errors;
  }, [showErrors, data]);

  const mismatchCount = Object.keys(mismatches).length;

  const rf = (label: string, value: ReviewFieldProps["value"], fieldKey?: string, stackOnMobile?: boolean) => (
    <ReviewField
      label={label}
      value={value}
      fieldKey={fieldKey}
      allErrors={allErrors}
      mismatches={mismatches}
      showErrors={showErrors}
      stackOnMobile={stackOnMobile}
    />
  );

  const nrf = (label: string, value: ReviewFieldProps["value"], fieldKey?: string) => (
    <ReviewField
      label={label}
      value={value}
      fieldKey={fieldKey}
      allErrors={allErrors}
      mismatches={mismatches}
      showErrors={showErrors}
      nested
    />
  );

  const peoDisplayValue = data.hasPeo === "Yes" && data.peoType
    ? (PEO_LABELS[data.peoType] ?? "Yes")
    : data.hasPeo;

  // Step 7 visibility helpers (same rules as StepDocuments)
  const showRequiredDocs = isRequiredDocsVisible(data.totalEnrolledEmployees, data.replacingMedical);
  const showOwnerEligibility = isOwnerEligibilityVisible(data.ownershipAssignments);
  const isSmallGroup = (data.avgW2Employees20Weeks ?? 0) < 20;
  const showCOBRAFollowup = !isSmallGroup || data.offerCOBRAVoluntary === "Yes";

  // Scroll to the topmost error row each time Submit is clicked and validation fails.
  // Keyed on globalTouchCounter (not showErrors) so subsequent submits re-trigger the
  // scroll even after the first failed attempt has already flipped showErrors true.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (globalTouchCounter <= mountCounter) return;
    // Wait one frame so the freshly-styled error rows are in the DOM with their data-error attrs.
    const id = requestAnimationFrame(() => {
      const firstError = containerRef.current?.querySelector('[data-error="true"]');
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(id);
  }, [globalTouchCounter, mountCounter]);

  return (
    <div ref={containerRef}>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Review &amp; Submit</h1>
      <p className="text-gray-500 mb-6">Please verify all information before submitting</p>

      {/* Mismatch banner */}
      {mismatchCount > 0 && (
        <Alert
          type="warning"
          message={
            <><strong>{mismatchCount} {mismatchCount === 1 ? "item differs" : "items differ"} from the proposal.</strong>{" "}
            Differences are highlighted below. Your entries will be treated as the source of truth.</>
          }
          className="mb-6"
        />
      )}

      {/* ── Page 1: Company Information ─────────────────────────────────── */}
      <ReviewSection title="Company Information" step={1} onEdit={setStep}>
        {rf("Coverage Effective Date", data.effectiveDate, "effectiveDate")}
        {rf("Company Legal Name", data.companyLegalName, "companyLegalName")}
        {rf("DBA (Doing Business As)", data.dba || "—")}
        {rf("Employer Federal Tax ID", data.federalTaxId, "federalTaxId")}
        {rf("Street Address", data.streetAddress, "streetAddress")}
        {rf("City", data.city, "city")}
        {rf("State", data.state, "state")}
        {rf("Zip Code", data.zipCode, "zipCode")}
        {rf("Organization Type", data.orgType, "orgType")}
        {rf("SIC Code", data.sicCode ? `${data.sicCode}${data.sicDescription ? ` — ${data.sicDescription}` : ""}` : "", "sicCode")}
        {rf("Year Business Established", data.yearEstablished, "yearEstablished")}
        {rf("Avg W-2 Employees (typical business days)", data.avgW2Employees, "avgW2Employees")}
        {rf("Avg W-2 Employees (20+ weeks)", data.avgW2Employees20Weeks, "avgW2Employees20Weeks")}
      </ReviewSection>

      {/* ── Page 2: Contacts ─────────────────────────────────────────────── */}
      <ReviewSection title="Contacts" step={2} onEdit={setStep}>
        {rf("Company Contact – Full Name", data.contactName, "contactName")}
        {rf("Company Contact – Title", data.contactTitle, "contactTitle")}
        {rf("Company Contact – Phone", data.contactPhone, "contactPhone")}
        {rf("Company Contact – Email", data.contactEmail, "contactEmail", true)}

        {rf("Fiduciary – Same as Company Contact?", data.fiduciarySameAsContact, "fiduciarySameAsContact")}
        {data.fiduciarySameAsContact === "No" && (
          <>
            {rf("Fiduciary – Full Name", data.fiduciaryName, "fiduciaryName")}
            {rf("Fiduciary – Title", data.fiduciaryTitle, "fiduciaryTitle")}
            {rf("Fiduciary – Phone", data.fiduciaryPhone, "fiduciaryPhone")}
            {rf("Fiduciary – Email", data.fiduciaryEmail, "fiduciaryEmail", true)}
          </>
        )}
        {rf("Need to add an alternate contact?", data.needAlternatePortalAccess, "needAlternatePortalAccess")}
        {data.needAlternatePortalAccess === "Yes" && (
          <>
            {rf("Alternate Portal Contact – Name", data.portalContactName, "portalContactName")}
            {rf("Alternate Portal Contact – Email", data.portalContactEmail, "portalContactEmail", true)}
            {rf("Alternate Portal Contact – Phone", data.portalContactPhone, "portalContactPhone")}
            {data.fiduciarySameAsContact === "No" && rf("Who should this person replace?", data.portalReplaces, "portalReplaces")}
          </>
        )}
      </ReviewSection>

      {/* ── Page 3a: Company Structure ────────────────────────────────────── */}
      <ReviewSection title="Company Structure" step={3} onEdit={setStep}>
        {rf("Collective Bargaining (CBA)?", data.hasCba, "hasCba")}
        {data.hasCba === "Yes" && (
          <>
            {rf("CBA Name", data.cbaUnitName, "cbaUnitName")}
            {rf("CBA Effective Date", data.cbaEffectiveDate, "cbaEffectiveDate")}
          </>
        )}
        {rf("PEO / Employee Leasing?", peoDisplayValue, "hasPeo")}
        {rf("Multi-Employer Plan?", data.isMultiEmployer, "isMultiEmployer")}
        {data.isMultiEmployer === "Yes" && rf(
          "Multi-Employer Type",
          MULTI_EMPLOYER_LABELS[data.multiEmployerType] ?? data.multiEmployerType,
          "multiEmployerType",
        )}
        {data.isMultiEmployer === "No" && rf(
          "Part of a commonly controlled group?",
          data.isCommonlyControlledGroup,
          "isCommonlyControlledGroup",
        )}
        {/* Per-entity Plan Structure blocks (ADO-3492 §2) — only rendered when
            an affiliated entity list exists (either multi-employer commonly
            controlled, or single-employer + Commonly Controlled Group = Yes).
            In the solo-primary path this block is suppressed entirely; the
            primary's ownership still appears in the Ownership Breakdown section. */}
        {isAffiliatedListShown(data) && (() => {
          const blocks = buildEntityBlocks(data);
          // Cross-row "at least one covered" rule — mirrors the live red alert
          // at the bottom of the Structure page's entity list. When active, we
          // flag every affiliated entity's Covered row red (visual only, via
          // ReviewField's `danger` prop) and render ONE aggregate message once
          // below the whole entity list, rather than duplicating the same text
          // under every row. The single-employer + CC-Group=Yes path locks
          // Covered to No by design, so the rule doesn't apply there.
          const coveredRequiredMissing =
            showErrors
            && data.isMultiEmployer === "Yes"
            && data.multiEmployerType === COMMONLY_CONTROLLED
            && data.affiliatedGroups.length > 0
            && !data.affiliatedGroups.some((g) => g.covered === true);
          return (
            <>
              {blocks.map((b) => {
                const nameError = b.errorPrefix ? allErrors[`${b.errorPrefix}name`] : undefined;
                // For affiliated entities during a covered-required violation,
                // suppress the per-row "Covered is required" text (drop the
                // fieldKey) and drive the red styling via `danger` instead —
                // the single aggregate footer below carries the message for
                // the whole group.
                const useAggregateCoveredHighlight = coveredRequiredMissing && !b.isPrimary;
                return (
                  <div key={b.id}>
                    <div
                      data-error={nameError ? "true" : undefined}
                      className={`px-3 pb-1 pt-3 ${nameError ? "bg-danger-bg" : ""}`}
                    >
                      <p className={`pl-3 text-sm font-semibold break-words ${nameError ? "text-danger" : "text-gray-800"}`}>
                        {entityHeaderLabel(b)}
                        {nameError && <span className="block text-xs font-normal mt-0.5">{nameError}</span>}
                      </p>
                    </div>
                    {nrf("Federal Tax ID", b.taxId, b.errorPrefix ? `${b.errorPrefix}taxId` : undefined)}
                    {nrf(
                      "Eligible Employees",
                      b.employees != null ? String(b.employees) : "",
                      b.isPrimary ? "primaryEligibleEmployees" : b.errorPrefix ? `${b.errorPrefix}employees` : undefined,
                    )}
                    <ReviewField
                      label="Covered Under Policy"
                      value={b.covered === true ? "Yes" : b.covered === false ? "No" : ""}
                      fieldKey={useAggregateCoveredHighlight ? undefined : b.errorPrefix ? `${b.errorPrefix}covered` : undefined}
                      allErrors={allErrors}
                      mismatches={mismatches}
                      showErrors={showErrors}
                      nested
                      danger={useAggregateCoveredHighlight}
                    />
                  </div>
                );
              })}
              {coveredRequiredMissing && (
                <Alert
                  type="error"
                  className="mx-3 my-2"
                  message="It is required for at least one entity other than the primary entity to be covered under the Optimyl policy."
                />
              )}
            </>
          );
        })()}
      </ReviewSection>

      {/* ── Page 3c: Ownership Breakdown (ADO-3492 §3) ───────────────────── */}
      <ReviewSection title="Ownership Breakdown" step={3} onEdit={setStep}>
        {(() => {
          const blocks = buildOwnershipBlocks(data);
          const ownerLabelById = new Map(data.owners.map((o, idx) => [o.id, ownerName(o, idx)]));
          return blocks.map((b) => {
            const rows = data.ownershipAssignments.filter((a) => a.entityId === b.id);
            const total = Math.round(rows.reduce((sum, r) => sum + (Number(r.ownership) || 0), 0) * 100) / 100;
            const totalKey = `ownershipAssignments.${b.id}.total`;
            return (
              <div key={b.id}>
                <div className="px-3 pb-1 pt-3">
                  <p className="pl-3 text-sm font-semibold break-words text-gray-800">
                    {entityHeaderLabel(b)}
                  </p>
                </div>
                <ReviewField
                  label="Total Ownership"
                  value={`${total}%`}
                  fieldKey={totalKey}
                  allErrors={allErrors}
                  mismatches={mismatches}
                  showErrors={showErrors}
                  nested
                />
                {rows.map((a) => {
                  const label = ownerLabelById.get(a.ownerId) ?? "Unassigned";
                  const w2 = a.receivingW2Wages === true ? "Yes" : a.receivingW2Wages === false ? "No" : "—";
                  const enr = a.enrollingInBenefits === true ? "Yes" : a.enrollingInBenefits === false ? "No" : "—";
                  return (
                    <ReviewField
                      key={a.id}
                      label={label}
                      value={`${a.ownership || 0}% (W-2: ${w2}, Enrolling: ${enr})`}
                      fieldKey={`ownershipAssignments.${a.id}.row`}
                      allErrors={allErrors}
                      mismatches={mismatches}
                      showErrors={showErrors}
                      nested
                    />
                  );
                })}
              </div>
            );
          });
        })()}
      </ReviewSection>

      {/* ── Page 4: Coverage & Benefits ──────────────────────────────────── */}
      <ReviewSection title="Coverage &amp; Benefits" step={4} onEdit={setStep}>
        {rf("Are you offering Optimyl Dental?", data.offeringDental, "offeringDental")}
        {rf("Are you offering Optimyl Vision?", data.offeringVision, "offeringVision")}
        {rf("Member Deductible Year Type", data.deductibleYearType, "deductibleYearType")}
        {rf("Employer Medical Contribution", data.medicalContribution != null ? `${data.medicalContribution}%` : "", "medicalContribution")}
        {rf("Will Optimyl replace current group Medical coverage?", data.replacingMedical, "replacingMedical")}
        {data.replacingMedical === "Yes" && (
          <>
            {rf("Prior Medical Carrier", data.priorMedicalCarrier === "Other" ? (data.priorMedicalCarrierOther || "Other") : data.priorMedicalCarrier, "priorMedicalCarrier")}
            {rf("Prior Medical Plan Type", data.priorMedicalPlanType, "priorMedicalPlanType")}
            {rf("Prior Medical Effective Date", data.priorMedicalEffectiveDate, "priorMedicalEffectiveDate")}
          </>
        )}
        {data.offeringDental === "Yes" && rf("Will Optimyl replace current group Dental coverage?", data.replacingDental, "replacingDental")}
        {rf("Reimburse Copays / OOP?", data.reimburseCopays, "reimburseCopays")}
        {rf("Offer HSA?", data.offerHsa, "offerHsa")}
        {rf("Offer HRA?", data.offerHra, "offerHra")}
        {rf("Other OOP Reimbursement?", data.otherReimbursement, "otherReimbursement")}
      </ReviewSection>

      {/* ── Page 5: Eligibility ──────────────────────────────────────────── */}
      <ReviewSection title="Eligibility &amp; Elections" step={5} onEdit={setStep}>
        {rf("Exclude Employee Class?", data.excludeClass, "excludeClass")}
        {data.excludeClass === "Yes" && (
          <>
            {rf("Excluded employee class", stackedList(data.excludedClasses), "excludedClasses")}
            {data.excludedClasses.includes("Other") && rf("Specify excluded class", data.excludedClassesOther, "excludedClassesOther")}
          </>
        )}

        {rf("Allow Domestic Partners to be eligible?", data.allowDomesticPartners, "allowDomesticPartners")}
        {rf("Minimum Hours per Week", data.minHoursPerWeek, "minHoursPerWeek")}
        {rf("Waiting Period", data.waitingPeriod, "waitingPeriod")}
        {rf("Waive Waiting Period for Current Employees?", data.waiveWaitingPeriod, "waiveWaitingPeriod")}
        {data.waiveWaitingPeriod === "No" && rf("Employees in Waiting Period?", data.employeesInWaitingPeriod, "employeesInWaitingPeriod")}
        {isSmallGroup && rf("Voluntary COBRA?", data.offerCOBRAVoluntary, "offerCOBRAVoluntary")}
        {showCOBRAFollowup && (
          <>
            {rf("Employees on COBRA?", data.employeesOnCOBRA, "employeesOnCOBRA")}
            {rf("COBRA Admin (Optimyl vs Self)?", data.useOptimylCOBRA === "Yes" ? "Optimyl" : data.useOptimylCOBRA === "No" ? "Self" : "", "useOptimylCOBRA")}
          </>
        )}
        {rf("Want Optimyl-Provided Section 125 Plan?", data.wantOptimylSection125, "wantOptimylSection125")}
        {data.wantOptimylSection125 === "Yes" && (
          <>
            {rf("Section 125 – Plans to Include", stackedList([
              data.section125PlanMedical && "Medical",
              data.section125PlanDental && "Dental",
              data.section125PlanVision && "Vision",
            ].filter(Boolean) as string[]), "section125PlanMedical")}
            {rf("Section 125 – Tiers to Include", stackedList([
              data.section125TierEmployeeOnly && "Employee Only",
              data.section125TierEmployeeSpouse && "Employee & Spouse",
              data.section125TierEmployeeChildren && "Employee & Children",
              data.section125TierEmployeeFamily && "Employee & Family",
            ].filter(Boolean) as string[]), "section125TierEmployeeOnly")}
          </>
        )}
      </ReviewSection>

      {/* ── Page 6: Payment ──────────────────────────────────────────────── */}
      <ReviewSection title="Payment" step={6} onEdit={setStep}>
        {rf("Payment Method", data.paymentMethod, "paymentMethod")}
        {data.paymentMethod === "ACH" && (
          <>
            {rf("Account Holder Name", data.achAccountHolderName, "achAccountHolderName")}
            {rf("Bank Name", data.achBankName, "achBankName")}
            {rf("ACH Routing (ABA) Number", data.achRoutingNumber ? `••••${data.achRoutingNumber.slice(-4)}` : "", "achRoutingNumber")}
            {rf("Account Number", data.achAccountNumber ? `••••${data.achAccountNumber.slice(-4)}` : "", "achAccountNumber")}
            {rf("Account Type", data.achAccountType, "achAccountType")}
          </>
        )}
        {data.paymentMethod === "Check" && (
          rf("Binder Check", data.documents.binderCheck?.name || "", "documents.binderCheck")
        )}
      </ReviewSection>

      {/* ── Page 7: Documents & Census ───────────────────────────────────── */}
      <ReviewSection title="Documents &amp; Census" step={7} onEdit={setStep}>
        {showRequiredDocs && (
          <>
            {rf("Employment Verification Document Type", data.docVerificationType, "docVerificationType")}
            {data.docVerificationType === "State Wage and Tax" && rf("State Wage & Tax Document", data.documents.wageTax?.name || "", "documents.wageTax")}
            {data.docVerificationType === "Payroll Records" && rf("Payroll Records Document", data.documents.payroll?.name || "", "documents.payroll")}
            {data.docVerificationType === "Business in operation less than 90 days" && rf("New Business Documentation", data.documents.newBusinessDocs?.name || "", "documents.newBusinessDocs")}
          </>
        )}
        {data.replacingMedical === "Yes" && rf("Prior Carrier Bill", data.documents.priorBill?.name || "", "documents.priorBill")}
        {showOwnerEligibility && !showRequiredDocs && rf("Company Established Within Last 90 Days?", data.newlyFormedCompany, "newlyFormedCompany")}
        {data.ownerEligibilityDocs.length > 0 && rf("Owner Eligibility Documents", data.ownerEligibilityDocs.map((d) => d.name).join(", "))}
        {/* Claims mode: the census file IS the required artifact — always render the row (with a
            fieldKey) so it participates in the "red-highlight the missing field" flow on submit
            (ADO-3484). Non-Claims mode: keep the existing show-only-if-uploaded behavior; the
            required data path for non-Claims is the Census Members list rendered below. */}
        {uwMethod === "Claims"
          ? rf("Employee Census File", data.documents.censusFile?.name || "", "documents.censusFile")
          : data.documents.censusFile && rf("Employee Census File", data.documents.censusFile.name)}
        {uwMethod !== "Claims" && rf(
          "Census Members",
          data.employees.length > 0
            ? `${data.employees.length} member${data.employees.length !== 1 ? "s" : ""}`
            : "",
          "employees",
        )}
      </ReviewSection>
    </div>
  );
}
